import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type Ref,
} from "react";
import type { SceneCompletion } from "../../../types/navigation";
import {
  useVideoCuePoints,
  type CuePoint,
  type OnCue,
} from "../hooks/useCuePoints";
import { useOnCompleteVideo } from "../hooks/useOnCompleteVideo";
import { usePlayAtStartTime } from "../hooks/usePlayAtStartTime";
import isDevMode from "../../../util/isDevMode";
import type { ActionCallsType } from "../../../services/actionCalls";
import "./videoPlayer.css";

export interface VideoPlayerHandle {
  seek(time: number): void;
  play(): void;
  pause(): void;
  isPlaying(): boolean;
}

type ComponentProps = {
  id?: string;
  src?: string;
  startTime?: number | undefined;
  cuePoints?: CuePoint[];
  onCue?: OnCue;
  onComplete: SceneCompletion | undefined;
  changeScene: (sceneId: string) => void;
  showSubtitles: boolean;
  ref?: Ref<VideoPlayerHandle>;
  onPlayingChange?: (isPlaying: boolean) => void;
  onPlayRejected?: () => void;
  onActionCall: ActionCallsType;
  children?: React.ReactNode;
};

export function VideoPlayer({
  id,
  src,
  startTime = 0,
  cuePoints,
  onCue,
  onComplete = undefined,
  changeScene,
  showSubtitles,
  ref,
  onPlayingChange,
  onPlayRejected,
  onActionCall,
  children,
}: ComponentProps) {
  const videoRef = useRef<HTMLVideoElement>(
    null,
  ) as React.RefObject<HTMLVideoElement>;
  const [isReady, setIsReady] = useState(startTime === 0);
  const devMode = isDevMode();

  // Keep the latest rejection callback without making the play callback
  // depend on a prop that changes on every parent render.
  const onPlayRejectedRef = useRef(onPlayRejected);
  useEffect(() => {
    onPlayRejectedRef.current = onPlayRejected;
  }, [onPlayRejected]);

  const playVideo = useCallback(() => {
    videoRef.current?.play().catch(() => onPlayRejectedRef.current?.());
  }, []);

  useVideoCuePoints(videoRef, cuePoints, onCue || (() => {}));

  const _seek = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      playVideo();
    }
  };

  const _isPlaying = () => {
    const video = videoRef.current;
    return (!!video && !video.paused) || false;
  };

  useImperativeHandle(ref, () => ({
    seek(time: number) {
      _seek(time);
    },
    play() {
      playVideo();
    },
    pause() {
      videoRef.current?.pause();
    },
    isPlaying() {
      return _isPlaying();
    },
  }));

  useOnCompleteVideo({
    videoRef,
    onComplete,
    changeScene,
    onActionCall,
    seek: _seek,
    id,
  });

  usePlayAtStartTime({ videoRef, startTime, setIsReady, src, play: playVideo });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const applySubtitles = () => {
      const track = video.textTracks[0];
      if (track) {
        track.mode = showSubtitles ? "showing" : "hidden";
      }
    };

    applySubtitles();
    video.addEventListener("loadedmetadata", applySubtitles);

    return () => {
      video.removeEventListener("loadedmetadata", applySubtitles);
    };
  }, [showSubtitles, id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => onPlayingChange?.(true);
    const handlePause = () => onPlayingChange?.(false);

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, [onPlayingChange, src]);

  return (
    <video
      ref={videoRef}
      className="video-player"
      src={src}
      preload="metadata"
      crossOrigin="anonymous"
      // muted
      playsInline
      disablePictureInPicture
      controls={devMode}
      style={{
        visibility: isReady ? "visible" : "hidden",
        pointerEvents: devMode ? "auto" : "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {children}
    </video>
  );
}
